import React, { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Palette, Footprints, Layers, Wrench, Sparkles, Table2,
  Home, Image as ImageIcon, HelpCircle, Star, FolderOpen, Settings, LogOut, Inbox,
  Menu, X, FileText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl } from "@/lib/api";
import { BonkerLogo } from "@/components/BonkerLogo";

interface NavItem { to: string; label: string; icon: any; end?: boolean; }
interface NavGroup { title?: string; items: NavItem[]; }

const GROUPS: NavGroup[] = [
  { items: [{ to: "/cms", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  { items: [{ to: "/cms/richieste", label: "Richieste", icon: Inbox }] },
  {
    title: "Configuratore",
    items: [
      { to: "/cms/temi", label: "Temi", icon: Palette },
      { to: "/cms/modelli", label: "Modelli", icon: Footprints },
      { to: "/cms/aree", label: "Aree", icon: Layers },
      { to: "/cms/tipologie", label: "Tipologie", icon: Wrench },
      { to: "/cms/dettagli", label: "Dettagli", icon: Sparkles },
      { to: "/cms/prezzi", label: "Matrice Prezzi", icon: Table2 },
    ],
  },
  {
    title: "Contenuti",
    items: [
      { to: "/cms/homepage", label: "Homepage", icon: Home },
      { to: "/cms/pagine", label: "Pagine", icon: FileText },
      { to: "/cms/portfolio", label: "Portfolio", icon: FolderOpen },
      { to: "/cms/faq", label: "FAQ", icon: HelpCircle },
      { to: "/cms/recensioni", label: "Cosa dicono i clienti", icon: Star },
      { to: "/cms/media", label: "Media Library", icon: ImageIcon },
    ],
  },
  { title: "Sistema", items: [{ to: "/cms/impostazioni", label: "Impostazioni", icon: Settings }] },
];

const SidebarNav: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => (
  <nav className="flex-1 py-4 overflow-y-auto" data-testid="cms-sidebar-nav">
    {GROUPS.map((g, i) => (
      <div key={i} className="mb-4">
        {g.title && (
          <p className="px-6 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground/50">
            {g.title}
          </p>
        )}
        {g.items.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={`nav-${item.to.split("/").pop()}`}
              className={({ isActive }) =>
                `group w-full flex items-center gap-3 px-6 py-2.5 text-sm font-medium border-l-2 transition-colors ${
                  isActive
                    ? "border-primary text-foreground bg-white/5"
                    : "border-transparent text-muted-foreground hover:text-secondary"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          );
        })}
      </div>
    ))}
  </nav>
);

export const CmsLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const [logo, setLogo] = useState<string | null>(null);
  const [siteName, setSiteName] = useState("Bonker");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      setLogo(data.logo_url);
      setSiteName(data.site_name);
    }).catch(() => {});
  }, []);

  const doLogout = () => { logout(); window.location.assign("/"); };
  const logoEl = <BonkerLogo src={logo ? mediaUrl(logo) : null} />;

  return (
    <div className="min-h-screen bg-background text-foreground grid grid-cols-1 md:grid-cols-[16rem_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col bg-black border-r border-white/10 sticky top-0 h-screen">
        <div className="h-16 flex items-center px-6 border-b border-white/10 shrink-0">{logoEl}</div>
        <SidebarNav />
        <div className="p-4 border-t border-white/10 shrink-0">
          <button
            data-testid="sidebar-logout-button"
            onClick={doLogout}
            className="w-full flex items-center gap-3 px-2 py-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="cms-mobile-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 max-w-[80%] flex flex-col bg-black border-r border-white/10 h-full">
            <div className="h-16 flex items-center justify-between px-6 border-b border-white/10 shrink-0">
              {logoEl}
              <button aria-label="Chiudi menu" data-testid="cms-drawer-close" onClick={() => setMobileOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarNav onNavigate={() => setMobileOpen(false)} />
            <div className="p-4 border-t border-white/10 shrink-0">
              <button
                onClick={doLogout}
                className="w-full flex items-center gap-3 px-2 py-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <LogOut className="h-4 w-4" /> Esci
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex flex-col min-h-screen">
        <header className="h-16 sticky top-0 z-40 bg-background border-b border-white/10 flex items-center justify-between px-6 md:px-8">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden text-foreground p-1 -ml-1"
              aria-label="Apri menu"
              data-testid="cms-mobile-menu-button"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="md:hidden">{logoEl}</div>
          </div>
          <p className="hidden md:block text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
            CMS · {siteName}
          </p>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold leading-none">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <div className="h-9 w-9 bg-primary text-primary-foreground flex items-center justify-center font-display font-black">
              {user?.name?.[0]?.toUpperCase() ?? "A"}
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 md:p-8" data-testid="cms-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
